import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUser, FiX } from 'react-icons/fi';
import { cpApi } from '../api/api';

function getApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: string | { error?: string } }; message?: string };
  const d = e?.response?.data;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (d && typeof d === 'object' && 'error' in d && typeof (d as { error: string }).error === 'string') {
    return (d as { error: string }).error;
  }
  return e?.message || fallback;
}

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

const field =
  'w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 shadow-inner focus:outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:placeholder:text-slate-500 dark:focus:border-emerald-400/40 transition-[border-color,box-shadow]';
const labelCls =
  'block text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-1.5 dark:text-slate-400';

export default function VndAddTeamMemberFormWidget({ onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !login.trim() || !password.trim()) {
      setError(t('addMember.validation'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await cpApi.addVndTeamMember({
        name: name.trim(),
        login: login.trim(),
        password: password.trim(),
      });
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: t('addMember.addedToast'), type: 'success' } }));
      onSaved();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, t('addMember.addError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-white/10">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/90 to-teal-600 shadow-lg shadow-emerald-900/20 dark:shadow-emerald-900/30">
            <FiUser className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">{t('addMember.title')}</h2>
            <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">{t('addMember.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
          aria-label={t('common.close')}
        >
          <FiX className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}
        <div>
          <label className={labelCls}>{t('addMember.name')}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={field}
            placeholder={t('addMember.namePlaceholder')}
            autoComplete="name"
          />
        </div>
        <div>
          <label className={labelCls}>{t('addMember.login')}</label>
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            className={`${field} font-mono text-[13px]`}
            placeholder={t('addMember.loginPlaceholder')}
            autoComplete="off"
          />
        </div>
        <div>
          <label className={labelCls}>{t('addMember.password')}</label>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${field} font-mono text-[13px]`}
            placeholder={t('addMember.passwordPlaceholder')}
            type="password"
            autoComplete="new-password"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-white/15 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/15 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 dark:shadow-emerald-900/30 transition-colors"
          >
            {saving ? t('common.saving') : t('addMember.submitBtn')}
          </button>
        </div>
      </form>
    </div>
  );
}
